import { apiFetch, apiFetchBlob, apiUploadFormData } from "@/lib/api";
import type { FirmwareReleaseNoteProductLine } from "./firmwareReleaseNoteSelect";

export const FIRMWARE_WORKFLOW_STATES = [
  "Received",
  "In Test",
  "Approved",
  "Rejected",
  "At Beta Site",
  "Production",
  "Obsolete",
  "Recalled",
] as const;

export type FirmwareWorkflowState = (typeof FIRMWARE_WORKFLOW_STATES)[number];

export { FIRMWARE_WORKFLOW_STAGE_FLOW } from "./firmwareWorkflowStageFlow";
export {
  FIRMWARE_RELEASE_NOTE_PRODUCT_LINES,
  firmwareReleaseNoteProductLineLabel,
  firmwareReleaseNoteSelectLabel,
  firmwareReleaseNoteToSelectOption,
  withFirmwareReleaseNoteSelectOption,
} from "./firmwareReleaseNoteSelect";
export type {
  FirmwareReleaseNoteProductLine,
  FirmwareReleaseNoteSelectOption,
} from "./firmwareReleaseNoteSelect";

export type FirmwareTransitionAction =
  | "start_testing"
  | "approve"
  | "reject"
  | "send_to_beta"
  | "put_in_production"
  | "obsolete"
  | "recall";

export type FirmwareOverflowAction =
  | "reject"
  | "send_to_beta"
  | "mark_internal_only"
  | "clear_internal_only";

export type FirmwareWorkflowAction = FirmwareTransitionAction | FirmwareOverflowAction;

export type FirmwareAllowedAction = {
  action: FirmwareTransitionAction;
  label: string;
  to: FirmwareWorkflowState;
};

export function firmwareWorkflowActionVariant(
  action: FirmwareWorkflowAction,
): "primary" | "secondary" | "danger" {
  if (action === "reject" || action === "recall") return "danger";
  if (action === "start_testing" || action === "approve" || action === "put_in_production") return "primary";
  return "secondary";
}

export type FirmwareOverflowActionOption = {
  action: FirmwareOverflowAction;
  label: string;
};

export type FirmwareSerialDeviceSkuCount = {
  sku: string;
  count: number;
};

export type FirmwareBetaCustomer = {
  fsAccountId: number;
  name: string;
};

export type FirmwareRecord = {
  id: number;
  orgId: number;
  version: string;
  erpnextName: string | null;
  releasedOn: string | null;
  approvedByName: string | null;
  approvedByUserId: string | null;
  approvedOn: string | null;
  appliesTo: string;
  pcbaFamily: FirmwarePcbaFamily | null;
  feature: FirmwareFeature | null;
  revisionEffective: number | null;
  revisionEnding: number | null;
  revisionEffectiveUnknown: boolean;
  changes: string;
  sentToBeta: boolean;
  betaCustomers: FirmwareBetaCustomer[];
  betaNotes: string | null;
  betaSerials: string[];
  amendedFromId: number | null;
  workflowState: FirmwareWorkflowState;
  hasFile: boolean;
  fileName: string | null;
  contentType: string | null;
  byteSize: number | null;
  contentEditable: boolean;
  allowedActions: FirmwareAllowedAction[];
  overflowActions: FirmwareOverflowActionOption[];
  internalOnly: boolean;
  erpnextCreatedAt: string | null;
  erpnextModifiedAt: string | null;
  erpnextOwner: string | null;
  createdAt: string;
  updatedAt: string;
  createdByUserId: string | null;
  updatedByUserId: string | null;
  releaseNoteId: number | null;
  releaseNoteVersionKey: string | null;
  releaseNoteProductLine: FirmwareReleaseNoteProductLine | null;
  releaseNoteNotes: string | null;
  serialDeviceCount: number | null;
  serialDeviceCountBySku: FirmwareSerialDeviceSkuCount[];
  canDownload: boolean;
};

export type FirmwareActivity = {
  id: number;
  firmwareId: number;
  activityAt: string;
  actorUserId: string | null;
  actorEmail: string;
  actionType: string;
  fieldName: string | null;
  oldValue: string | null;
  newValue: string | null;
  note: string | null;
};

export const FIRMWARE_LIST_KEY = ["engineering", "firmware"] as const;
export const FIRMWARE_SERIAL_DEVICE_COUNTS_KEY = ["engineering", "firmware", "serial-device-counts"] as const;

export const FIRMWARE_SERIAL_DEVICE_COUNT_HOVER =
  "Active devices whose logged firmware version matches this release-note version key.";

export const FIRMWARE_FILE_SERIAL_DEVICE_COUNT_HOVER =
  "Active devices whose logged firmware version matches this file.";

export function firmwareDetailKey(id: number) {
  return ["engineering", "firmware", id] as const;
}

export function firmwareWorkflowTone(
  state: FirmwareWorkflowState,
): "neutral" | "info" | "success" | "warning" | "danger" {
  switch (state) {
    case "Production":
    case "Approved":
      return "success";
    case "In Test":
    case "At Beta Site":
      return "info";
    case "Received":
      return "warning";
    case "Rejected":
    case "Recalled":
      return "danger";
    case "Obsolete":
    default:
      return "neutral";
  }
}

export async function fetchFirmwareList(options: {
  q?: string;
  workflowState?: FirmwareWorkflowState | "all";
  releaseNoteId?: number | null;
  limit?: number;
  offset?: number;
}): Promise<{ firmware: FirmwareRecord[]; total: number; limit: number; offset: number }> {
  const params = new URLSearchParams();
  if (options.q) params.set("q", options.q);
  if (options.workflowState && options.workflowState !== "all") {
    params.set("workflowState", options.workflowState);
  }
  if (options.releaseNoteId != null) params.set("releaseNoteId", String(options.releaseNoteId));
  if (options.limit != null) params.set("limit", String(options.limit));
  if (options.offset != null) params.set("offset", String(options.offset));
  const query = params.toString();
  return apiFetch(`/api/engineering/firmware${query ? `?${query}` : ""}`);
}

export async function fetchFirmwareSerialDeviceCounts(): Promise<{
  counts: Array<{
    firmwareId: number;
    deviceCount: number;
    deviceCountBySku: FirmwareSerialDeviceSkuCount[];
  }>;
}> {
  return apiFetch("/api/engineering/firmware/serial-device-counts");
}

export async function fetchFirmwareDetail(
  id: number,
): Promise<{ firmware: FirmwareRecord; activities: FirmwareActivity[] }> {
  return apiFetch(`/api/engineering/firmware/${id}`);
}

export async function createFirmware(form: FormData): Promise<{ firmware: FirmwareRecord }> {
  return apiUploadFormData("/api/engineering/firmware", form);
}

export async function updateFirmware(
  id: number,
  form: FormData,
): Promise<{ firmware: FirmwareRecord | null }> {
  return apiUploadFormData(`/api/engineering/firmware/${id}`, form, { method: "PATCH" });
}

export async function transitionFirmware(
  id: number,
  action: FirmwareWorkflowAction,
  options?: {
    note?: string;
    betaCustomerFsAccountIds?: number[];
    betaNotes?: string | null;
    betaSerials?: string[];
  },
): Promise<{ firmware: FirmwareRecord | null; activities: FirmwareActivity[] }> {
  return apiFetch(`/api/engineering/firmware/${id}/transitions`, {
    method: "POST",
    body: {
      action,
      note: options?.note ?? null,
      betaCustomerFsAccountIds: options?.betaCustomerFsAccountIds,
      betaNotes: options?.betaNotes ?? null,
      betaSerials: options?.betaSerials,
    },
  });
}

export async function searchFirmwareBetaCustomers(
  q: string,
): Promise<{ rows: FirmwareBetaCustomer[] }> {
  const params = new URLSearchParams();
  if (q.trim() !== "") params.set("q", q.trim());
  return apiFetch(`/api/engineering/firmware/beta-customers?${params.toString()}`);
}

export type FirmwareCheckCellValue = string | number | boolean | null;

export type FirmwareCheckSection = {
  key: string;
  title: string;
  rows: Array<Record<string, FirmwareCheckCellValue>>;
};

export type FirmwareCheckRun = {
  id: number;
  firmwareId: number;
  fileSha256: string;
  status: "queued" | "running" | "complete" | "partial" | "failed" | "stale";
  toolVersion: string | null;
  error: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
};

export type FirmwareCheckDocument = {
  schema_version: number;
  tool_version: string;
  target: string;
  file_sha256: string;
  status: "complete" | "partial" | "failed";
  warnings: string[];
  sections: FirmwareCheckSection[];
};

export function firmwareCheckRunKey(firmwareId: number): readonly ["engineering", "firmware-check", number] {
  return ["engineering", "firmware-check", firmwareId];
}

export async function fetchLatestFirmwareCheckRun(id: number): Promise<{
  configured: boolean;
  notice: string;
  run: FirmwareCheckRun | null;
  result?: FirmwareCheckDocument | null;
}> {
  const latest = await apiFetch<{
    configured: boolean;
    notice: string;
    run: FirmwareCheckRun | null;
  }>(`/api/engineering/firmware/${id}/check-runs/latest`);
  if (latest.run == null || (latest.run.status !== "complete" && latest.run.status !== "partial" && latest.run.status !== "failed")) {
    return latest;
  }
  const detail = await apiFetch<{
    run: FirmwareCheckRun;
    result: FirmwareCheckDocument | null;
    notice: string;
  }>(`/api/engineering/firmware/${id}/check-runs/${latest.run.id}`);
  return { configured: latest.configured, notice: latest.notice, run: detail.run, result: detail.result };
}

export async function startFirmwareCheckRun(id: number): Promise<{ run: FirmwareCheckRun; notice: string }> {
  return apiFetch(`/api/engineering/firmware/${id}/check-runs`, { method: "POST" });
}

export async function runFirmwareCommandSequence(
  id: number,
  commands: string[],
): Promise<{ notice: string; result: FirmwareCheckDocument }> {
  return apiFetch(`/api/engineering/firmware/${id}/sequence`, {
    method: "POST",
    body: { commands },
  });
}

export async function downloadFirmwareFile(id: number, fallbackName: string): Promise<void> {
  const { blob, originalFilename } = await apiFetchBlob(`/api/engineering/firmware/${id}/file`);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = originalFilename || fallbackName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export type FirmwareShareGrant = {
  id: string;
  firmwareId: number;
  granteeUserId: string;
  granteeDisplayName: string | null;
  granteeEmail: string | null;
  createdAt: string;
};

export type FirmwareShareGranteeOption = {
  userId: string;
  label: string;
  email: string | null;
};

export function firmwareShareGrantsKey(firmwareId: number) {
  return ["engineering", "firmware", firmwareId, "shares"] as const;
}

export async function fetchFirmwareShares(firmwareId: number): Promise<{ grants: FirmwareShareGrant[] }> {
  return apiFetch(`/api/engineering/firmware/${firmwareId}/shares`);
}

export async function fetchFirmwareShareGrantees(q: string): Promise<{ grantees: FirmwareShareGranteeOption[] }> {
  const params = new URLSearchParams();
  if (q.trim() !== "") params.set("q", q.trim());
  return apiFetch(`/api/engineering/firmware/share-grantees?${params.toString()}`);
}

export async function createFirmwareShare(
  firmwareId: number,
  granteeUserId: string,
): Promise<{ grant: FirmwareShareGrant | null; created: boolean }> {
  return apiFetch(`/api/engineering/firmware/${firmwareId}/shares`, {
    method: "POST",
    body: { granteeUserId },
  });
}

export async function revokeFirmwareShare(firmwareId: number, grantId: string): Promise<void> {
  await apiFetch(`/api/engineering/firmware/${firmwareId}/shares/${encodeURIComponent(grantId)}`, {
    method: "DELETE",
  });
}

export const FIRMWARE_PCBA_FAMILIES = ["1485", "1509", "1607", "1609", "fi5a"] as const;

export type FirmwarePcbaFamily = (typeof FIRMWARE_PCBA_FAMILIES)[number];

export const FIRMWARE_FEATURES = ["W", "N", "W-HC", "Q", "N-B"] as const;

export type FirmwareFeature = (typeof FIRMWARE_FEATURES)[number];

export function firmwarePcbaFamilyLabel(family: FirmwarePcbaFamily): string {
  const canonical = family === "fi5a" ? "1485" : family;
  if (canonical === "1485") return "1485 (FI-5A)";
  if (canonical === "1509") return "1509 (FI-3C)";
  if (canonical === "1607") return "1607 (FI-3C)";
  return "1609 (DM-1)";
}

export const FIRMWARE_PCBA_FAMILY_CHOICES = ["1485", "1509", "1607", "1609"] as const;

export function firmwareFeaturesForFamily(family: FirmwarePcbaFamily | null): FirmwareFeature[] {
  if (family == null) return [];
  const canonical = family === "fi5a" ? "1485" : family;
  if (canonical === "1485") return ["W", "N", "W-HC"];
  if (canonical === "1607" || canonical === "1509") return ["Q", "N-B"];
  return [];
}

export function firmwareFeatureRequiredForFamily(family: FirmwarePcbaFamily): boolean {
  return firmwareFeaturesForFamily(family).length > 0;
}

/** Matches 3CPCBAFixture firmware_files names such as 6.59_1607_Q.hex. */
export function formatFirmwareFixtureStem(input: {
  versionKey: string;
  pcbaFamily: FirmwarePcbaFamily;
  feature: FirmwareFeature | null;
}): string {
  const versionKey = input.versionKey.trim();
  if (versionKey === "") return "";
  const family = input.pcbaFamily === "fi5a" ? "1485" : input.pcbaFamily;
  if (input.feature == null) {
    return `${versionKey}_${family}`;
  }
  return `${versionKey}_${family}_${input.feature}`;
}

export function formatFirmwareFixtureFileName(input: {
  versionKey: string;
  pcbaFamily: FirmwarePcbaFamily;
  feature: FirmwareFeature | null;
}): string {
  const stem = formatFirmwareFixtureStem(input);
  return stem === "" ? "" : `${stem}.hex`;
}

export function firmwareRecordFileName(row: {
  fileName: string | null;
  releaseNoteVersionKey?: string | null;
  pcbaFamily: FirmwarePcbaFamily | null;
  feature: FirmwareFeature | null;
}): string {
  if (row.releaseNoteVersionKey != null && row.releaseNoteVersionKey.trim() !== "" && row.pcbaFamily != null) {
    const named = formatFirmwareFixtureFileName({
      versionKey: row.releaseNoteVersionKey,
      pcbaFamily: row.pcbaFamily,
      feature: row.feature,
    });
    if (named !== "") return named;
  }
  if (row.fileName != null && row.fileName.trim() !== "") return row.fileName;
  return "";
}

export function firmwareRevisionEffectiveDisplay(row: {
  revisionEffective: number | null;
  revisionEffectiveUnknown: boolean;
}): string {
  if (row.revisionEffective != null) return `R${String(row.revisionEffective)}`;
  return "";
}

export function firmwareRevisionEndingDisplay(row: { revisionEnding: number | null }): string {
  if (row.revisionEnding == null) return "";
  return `R${String(row.revisionEnding)}`;
}

export type FirmwareReleaseNoteFileRef = {
  id: number;
  version: string;
  fileName: string | null;
  hasFile: boolean;
};

export type FirmwareReleaseNoteRecord = {
  id: number;
  orgId: number;
  versionKey: string;
  productLine: FirmwareReleaseNoteProductLine;
  notes: string;
  comments: string | null;
  releasedOn: string | null;
  firmwareCount: number;
  firmwareFiles: FirmwareReleaseNoteFileRef[];
  serialDeviceCount: number | null;
  serialDeviceCountBySku: FirmwareSerialDeviceSkuCount[];
  createdAt: string;
  updatedAt: string;
  createdByUserId: string | null;
  createdByDisplayName: string | null;
  updatedByUserId: string | null;
};

export const FIRMWARE_RELEASE_NOTES_LIST_KEY = ["engineering", "firmware-release-notes"] as const;
export const FIRMWARE_RELEASE_NOTE_SERIAL_DEVICE_COUNTS_KEY = [
  "engineering",
  "firmware-release-notes",
  "serial-device-counts",
] as const;

export function firmwareReleaseNoteDetailKey(id: number) {
  return ["engineering", "firmware-release-notes", id] as const;
}

export function firmwareReleaseNoteUploadedByLabel(row: {
  createdByDisplayName: string | null;
}): string {
  const name = row.createdByDisplayName?.trim() ?? "";
  return name === "" ? "Unknown" : name;
}

export async function fetchFirmwareReleaseNotes(options: {
  q?: string;
  productLine?: FirmwareReleaseNoteProductLine | "all";
  limit?: number;
  offset?: number;
}): Promise<{
  releaseNotes: FirmwareReleaseNoteRecord[];
  total: number;
  limit: number;
  offset: number;
}> {
  const params = new URLSearchParams();
  if (options.q) params.set("q", options.q);
  if (options.productLine && options.productLine !== "all") {
    params.set("productLine", options.productLine);
  }
  if (options.limit != null) params.set("limit", String(options.limit));
  if (options.offset != null) params.set("offset", String(options.offset));
  const query = params.toString();
  return apiFetch(`/api/engineering/firmware-release-notes${query ? `?${query}` : ""}`);
}

export async function fetchFirmwareReleaseNoteSerialDeviceCounts(): Promise<{
  counts: Array<{ versionKey: string; deviceCount: number; deviceCountBySku: FirmwareSerialDeviceSkuCount[] }>;
}> {
  return apiFetch("/api/engineering/firmware-release-notes/serial-device-counts");
}

export type FirmwareReleaseNoteCommentAttachment = {
  id: number;
  kind: "file" | "link";
  linkUrl: string | null;
  linkLabel: string | null;
  fileName: string | null;
  contentType: string | null;
  byteSize: number | null;
  createdAt: string;
};

export type FirmwareReleaseNoteComment = {
  id: number;
  releaseNoteId: number;
  body: string;
  createdByUserId: string | null;
  createdByDisplayName: string | null;
  createdAt: string;
  attachments: FirmwareReleaseNoteCommentAttachment[];
};

export function firmwareReleaseNoteCommentsKey(releaseNoteId: number) {
  return ["engineering", "firmware-release-notes", releaseNoteId, "comments"] as const;
}

export async function fetchFirmwareReleaseNoteComments(
  releaseNoteId: number,
): Promise<{ comments: FirmwareReleaseNoteComment[] }> {
  return apiFetch(`/api/engineering/firmware-release-notes/${releaseNoteId}/comments`);
}

export async function createFirmwareReleaseNoteComment(
  releaseNoteId: number,
  input: {
    body: string;
    links: Array<{ url: string; label?: string | null }>;
    files: File[];
  },
): Promise<{ comment: FirmwareReleaseNoteComment }> {
  const form = new FormData();
  form.set("body", input.body);
  if (input.links.length > 0) {
    form.set("links", JSON.stringify(input.links));
  }
  for (const file of input.files) {
    form.append("files", file, file.name);
  }
  return apiUploadFormData(`/api/engineering/firmware-release-notes/${releaseNoteId}/comments`, form, {
    method: "POST",
  });
}

export async function fetchFirmwareReleaseNoteDetail(
  id: number,
): Promise<{ releaseNote: FirmwareReleaseNoteRecord }> {
  return apiFetch(`/api/engineering/firmware-release-notes/${id}`);
}

export async function createFirmwareReleaseNote(body: {
  versionKey: string;
  productLine: FirmwareReleaseNoteProductLine;
  notes: string;
  comments?: string | null;
  releasedOn?: string | null;
}): Promise<{ releaseNote: FirmwareReleaseNoteRecord }> {
  return apiFetch("/api/engineering/firmware-release-notes", {
    method: "POST",
    body,
  });
}
